import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageTransition from '../components/PageTransition';

const serviceDetails = {
  tax: {
    title: 'Tax Preparation & Planning',
    details: `Comprehensive tax services for individuals and businesses of all sizes, with multi-jurisdictional expertise spanning federal, state, and local compliance.

            Core Services:

            - Individual tax preparation (federal and state), deduction and credit optimization
            - Business tax preparation for C-Corp, S-Corp, Partnership, and LLC entities
            - Multi-state and multi-jurisdictional tax filings
            - Sales tax, use tax, and payroll tax compliance
            - Proactive tax planning, projections, and quarterly estimated payments
            - IRS and state notice response, audit representation, and dispute resolution

            Specialized Capabilities:

            - Entity selection and restructuring for optimal tax efficiency
            - Stock compensation, RSU, and equity incentive plan taxation
            - Cryptocurrency and digital asset tax reporting
            - Real estate investment and 1031 exchange planning
            - K-1 coordination for partnerships and S-Corps
            - Prior-year amendments, penalty abatement, and back tax resolution
            - International tax considerations for cross-border operations`
  },
  accounting: {
    title: 'Accounting & Bookkeeping',
    details: `End-to-end accounting support designed to deliver financial clarity, compliance, and confidence in decision-making.

            - Full-cycle bookkeeping: transaction categorization, receipt management, and reconciliations
            - Accounts payable and accounts receivable processing and aging analysis
            - Monthly and quarterly close procedures with management-ready reporting
            - GAAP-compliant financial statement preparation and documentation
            - General ledger maintenance, tie-outs, and variance analysis

            Financial Reporting:

            - Income statement (P&L), balance sheet, and cash flow statement preparation
            - Custom KPI dashboards and management reporting packages
            - Budget-to-actual variance analysis and trend reporting
            - Unlimited consultations with your dedicated accounting team

            Outsourced Back-Office Support:

            - Complete outsourced accounting department for growing businesses
            - Chart of accounts design and optimization
            - Internal controls implementation and process documentation`
  },
  payroll: {
    title: 'Payroll Services',
    details: `Accurate, on-time payroll processing with full compliance management across all jurisdictions.

            - Payroll processing and direct deposit administration
            - Federal, state, and local payroll tax filings (941, 940, W-2, 1099)
            - New hire reporting and employment compliance
            - Year-end payroll reporting and reconciliation

            Advanced Capabilities:

            - Multi-state payroll with jurisdiction-specific tax handling
            - PTO accrual tracking and custom earnings/deduction configurations
            - Payroll journal entries and general ledger mapping
            - Workers compensation audit support
            - Benefits administration coordination`
  },
  audits: {
    title: 'Audit, Review & Compilation',
    details: `Independent assurance services designed to meet the requirements of lenders, investors, grantors, and regulatory bodies.

            - Financial statement audits conducted in accordance with GAAS
            - Review engagements providing limited assurance
            - Compilation services for management-use financial statements
            - Agreed-upon procedures engagements

            Common Use Cases:

            - Lender covenant compliance and bank reporting requirements
            - Investor and board of directors reporting
            - Grant compliance for non-profit organizations
            - Pre-transaction quality of earnings analysis
            - Regulatory and licensing compliance`
  },
  formation: {
    title: 'Entity Formation & Compliance',
    details: `Full-service business formation and ongoing compliance management to ensure your entity is properly structured and maintained.

            - Entity type selection consulting (LLC, S-Corp, C-Corp, Partnership, Sole Proprietorship)
            - State registration filings and name availability searches
            - EIN applications and federal tax elections (Form 2553, 8832)
            - Business licenses, permits, and industry-specific registrations
            - Sales tax permits and state payroll tax registrations

            Ongoing Compliance:

            - Annual report filings and registered agent services
            - Operating agreement and corporate bylaws coordination
            - Corporate minutes and recordkeeping
            - State franchise tax and annual fee compliance
            - Multi-state registration for expanding businesses`
  },
  financial: {
    title: 'CFO & Financial Advisory',
    details: `Executive-level financial leadership and strategic advisory without the overhead of a full-time hire.

            - Part-time and fractional CFO services
            - Financial modeling, budgeting, and multi-scenario forecasting
            - Cash flow management, working capital optimization, and liquidity planning
            - KPI dashboard design and performance monitoring
            - Board of directors and investor reporting packages

            Strategic Outcomes:

            - Improved unit economics and margin expansion strategies
            - Data-driven operating cadence with enhanced financial visibility
            - Capital raise preparation, investor materials, and pitch deck financial support
            - Pricing strategy analysis and profitability optimization
            - Exit planning and transaction readiness`
  },
  forensic: {
    title: 'Forensic Accounting',
    details: `Detailed financial investigation and analysis services for litigation support, fraud examination, and dispute resolution.

            - Fraud detection, investigation, and quantification
            - Embezzlement and asset misappropriation analysis
            - Litigation support and expert witness testimony
            - Partnership and shareholder dissolution accounting
            - Insurance claim analysis and business interruption calculations

            Specialized Areas:

            - Matrimonial and divorce financial analysis
            - Business damage quantification for commercial disputes
            - Tracing of funds and forensic data analysis
            - Internal controls assessment and fraud prevention recommendations
            - Regulatory investigation support and compliance reviews`
  },
  consulting: {
    title: 'Business Consulting',
    details: `Strategic business advisory services designed to optimize operations, facilitate transactions, and drive sustainable growth.

            - Mergers and acquisitions advisory and due diligence support
            - Business valuations for sale, estate planning, and litigation purposes
            - Financial projections and feasibility studies for business plans
            - Business structure optimization and restructuring
            - Operational efficiency analysis and process improvement

            Growth Advisory:

            - Market expansion planning and financial modeling
            - Succession planning and ownership transition strategies
            - Family business governance and inter-generational planning
            - Non-profit formation, 501(c)(3) applications, and governance consulting
            - Technology system selection and implementation advisory`
  },
  cfo: {
    title: 'Part-Time CFO Services',
    details: `Executive-level finance leadership without full-time overhead, tailored for growth-stage businesses.

            - Financial modeling and KPI dashboards
            - Budgeting, forecasting, and board reporting
            - Capital planning, fundraising support, and investor materials
            - Cash flow optimization and working capital management
            - Financial operations design and team mentorship`
  },
  succession: {
    title: 'Succession Planning',
    details: `Protect business continuity and maximize value through thoughtful ownership transition planning.

            - Ownership transition strategies and timeline development
            - Family business governance frameworks
            - Tax-efficient transfer structures and estate coordination
            - Buy-sell agreement financial analysis
            - Key person risk assessment and contingency planning`
  },
  nonprofit: {
    title: 'Non-Profit Organizations',
    details: `Specialized compliance, reporting, and advisory services for mission-driven organizations.

            - Form 990 preparation and annual compliance filings
            - Grant reporting, tracking, and compliance management
            - Fund accounting setup, policies, and procedures
            - Board reporting packages and financial governance
            - 501(c)(3) application support and tax-exempt status consulting`
  },
  quickbooks: {
    title: 'QuickBooks Setup & Support',
    details: `Expert configuration, optimization, and ongoing support for your QuickBooks environment.

            - Initial setup: chart of accounts, workflows, and third-party integrations
            - Hands-on training sessions for your accounting team
            - Troubleshooting, performance optimization, and cleanup services

            Advanced Support:

            - Bank feed configuration, rules, and automation best practices
            - Historical file cleanup and data migration
            - Custom report design and management dashboards
            - Multi-entity and inter-company setup`
  },
  salt: {
    title: 'State & Local Tax (SALT)',
    details: `Expert guidance navigating the complex landscape of state and local tax obligations across all 50 states.

            Core Services:

            - Nexus analysis and multi-state filing obligation assessments
            - Sales and use tax compliance, registration, and audit defense
            - State income tax planning and apportionment optimization
            - Voluntary disclosure agreements (VDAs) and amnesty programs
            - Property tax consulting and appeals

            Specialized Areas:

            - Unclaimed property compliance and reporting
            - State tax credit and incentive identification
            - Pass-through entity tax elections and optimization
            - Marketplace facilitator and economic nexus analysis
            - State and local tax controversy representation`
  },
  wealth: {
    title: 'Wealth & Estate Planning',
    details: `Comprehensive tax and advisory strategies for high-net-worth individuals, families, and fiduciaries.

            - Estate and gift tax planning and return preparation (Forms 706, 709)
            - Trust formation, administration, and fiduciary income tax (Form 1041)
            - Generational wealth transfer strategies and family governance
            - Charitable giving strategies and foundation management
            - Life insurance trust planning and funding analysis

            Advanced Planning:

            - Family limited partnership and LLC structuring
            - Grantor retained annuity trusts (GRATs) and qualified personal residence trusts (QPRTs)
            - Business succession and ownership transition planning
            - Pre-liquidity event tax planning for founders and executives
            - Cross-border estate and inheritance tax coordination`
  },
  rnd: {
    title: 'R&D Tax Credits',
    details: `Identification, documentation, and maximization of federal and state research and development tax credit opportunities under IRC Section 41.

            - Qualifying activity identification across all business functions
            - Four-part test analysis and contemporaneous documentation
            - Federal R&D credit computation (regular and alternative simplified methods)
            - State R&D credit identification and filing coordination
            - IRS audit defense and documentation support

            Industry Applications:

            - Software development and technology companies
            - Manufacturing process improvements and tooling
            - Engineering, architecture, and design firms
            - Life sciences, pharmaceuticals, and medical devices
            - Food and beverage formulation and process innovation
            - Construction and building science`
  },
  risk: {
    title: 'Risk Advisory & SOC Reporting',
    details: `Internal controls assessment, risk management, and compliance reporting services designed to strengthen governance and build stakeholder confidence.

            - SOC 1 (ICFR) examination and readiness assessments
            - SOC 2 (Trust Services Criteria) Type I and Type II examinations
            - Internal controls design, implementation, and testing
            - Enterprise risk management framework development
            - IT general controls review and cybersecurity risk assessment

            Compliance & Governance:

            - Regulatory compliance reviews (industry-specific)
            - Policy and procedure development and documentation
            - Board and audit committee advisory services
            - Third-party vendor risk assessment programs
            - Business continuity and disaster recovery planning`
  },
};

function renderDetails(text) {
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, idx) => {
    const trimmed = para.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('-')) {
      const items = trimmed.split(/\n/).filter(line => line.trim().startsWith('-')).map(line => line.trim().replace(/^-\s*/, ''));
      return (
        <ul key={idx} style={{
          margin:'16px 0', padding:0, listStyle:'none',
        }}>
          {items.map((item, i) => (
            <li key={i} style={{
              display:'flex', alignItems:'flex-start', gap:12,
              marginBottom:10, fontSize:'1rem', lineHeight:1.6,
              color:'var(--clr-text)', fontWeight:400,
            }}>
              <span style={{
                display:'inline-flex', alignItems:'center', justifyContent:'center',
                width:20, height:20, borderRadius:'50%',
                background:'var(--clr-offwhite)', color:'var(--clr-primary)',
                flexShrink:0, marginTop:3,
              }}>
                <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20,6 9,17 4,12"/>
                </svg>
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (trimmed.length < 60 && !trimmed.includes('.')) {
      const headerText = trimmed.endsWith(':') ? trimmed.slice(0, -1) : trimmed;
      const slug = headerText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      return (
        <h3 key={idx} id={slug} className="scrollspy-section" style={{
          fontFamily: 'var(--font-heading)',
          fontSize: '1.15rem',
          fontWeight: 600,
          color: 'var(--clr-navy)',
          marginTop: 32,
          marginBottom: 8,
          paddingBottom: 8,
          borderBottom: '1px solid var(--clr-border)',
        }}>{headerText}</h3>
      );
    }
    return <p key={idx} style={{
      margin:'16px 0', fontSize:'1.05rem', lineHeight:1.7,
      color:'var(--clr-text-light)', fontWeight:400,
    }}>{trimmed}</p>;
  });
}

function ServiceDetail() {
  const { id } = useParams();
  const service = serviceDetails[id];

  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    const sections = document.querySelectorAll('.scrollspy-section');
    sections.forEach((sec) => observer.observe(sec));

    return () => sections.forEach((sec) => observer.unobserve(sec));
  }, [service]);

  if (!service) return (
    <div style={{textAlign:'center', padding:'120px 24px'}}>
      <h2 style={{
        fontFamily: 'var(--font-heading)',
        fontSize: '2rem', color:'var(--clr-navy)', marginBottom:16,
      }}>Service Not Found</h2>
      <Link to="/" className="byhw-cta">Back to Home</Link>
    </div>
  );

  const tocSections = service ? service.details.split(/\n\n+/).filter(p => {
    const t = p.trim();
    return t && t.length < 60 && !t.includes('.') && !t.startsWith('-');
  }).map(p => {
    const title = p.trim().endsWith(':') ? p.trim().slice(0, -1) : p.trim();
    return {
      title,
      id: title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    };
  }) : [];

  return (
    <PageTransition>
      {/* Hero header */}
      <div style={{
        background: 'linear-gradient(135deg, var(--clr-navy) 0%, var(--clr-navy-deep) 100%)',
        padding: '80px 24px 56px',
        textAlign: 'center',
      }}>
        <div style={{
          maxWidth:800, margin:'0 auto 24px',
          display:'flex', justifyContent:'center', gap:8,
          fontSize:'0.85rem', color:'rgba(255,255,255,0.4)',
        }}>
          <Link to="/" style={{color:'rgba(255,255,255,0.5)', textDecoration:'none', transition:'color 0.2s'}}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
          >Services</Link>
          <span>/</span>
          <span style={{color:'rgba(255,255,255,0.7)'}}>{service.title}</span>
        </div>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 4vw, 2.8rem)',
          fontWeight: 700,
          color: 'var(--clr-white)',
          lineHeight: 1.15,
        }}>{service.title}</h1>
      </div>

      {/* Content */}
      <div className="service-detail-grid" style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '64px 32px 100px',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 300px',
        gap: '64px',
        alignItems: 'start'
      }}>
        {/* Main Content Column */}
        <div className="fade-in-up">
          {renderDetails(service.details)}
          
          <div style={{
            marginTop: 56,
            paddingTop: 32,
            borderTop: '1px solid var(--clr-border)',
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
            <Link to="/" style={{
              display:'inline-flex', alignItems:'center', gap:8,
              color:'var(--clr-primary)', textDecoration:'none', fontSize:'0.95rem',
              fontWeight:600, transition:'gap 0.3s ease', textTransform: 'uppercase', letterSpacing: '0.05em'
            }}
              onMouseEnter={e => e.currentTarget.style.gap = '12px'}
              onMouseLeave={e => e.currentTarget.style.gap = '8px'}
            >
              <span>&larr;</span> All Services
            </Link>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="fade-in-up" style={{
          position: 'sticky',
          top: '120px',
          background: 'var(--clr-offwhite)',
          padding: '32px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--clr-border)'
        }}>
          {tocSections.length > 0 && (
            <div style={{marginBottom: 24}}>
              <h4 style={{
                fontFamily: "var(--font-heading)",
                fontSize: '1.2rem',
                color: 'var(--clr-navy)',
                marginBottom: '16px'
              }}>On this page</h4>
              <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                {tocSections.map(sec => (
                  <li key={sec.id} style={{marginBottom: 8}}>
                    <a href={`#${sec.id}`} onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById(sec.id);
                      if(element) {
                        const y = element.getBoundingClientRect().top + window.scrollY - 100;
                        window.scrollTo({top: y, behavior: 'smooth'});
                      }
                    }} style={{
                      color: activeSection === sec.id ? 'var(--clr-accent-gold)' : 'var(--clr-text-light)',
                      fontWeight: activeSection === sec.id ? 700 : 400,
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      display: 'block',
                      transition: 'all 0.2s',
                      paddingLeft: activeSection === sec.id ? '10px' : '0',
                      borderLeft: activeSection === sec.id ? '3px solid var(--clr-accent-gold)' : '3px solid transparent',
                    }}>
                      {sec.title}
                    </a>
                  </li>
                ))}
              </ul>
              <div style={{borderTop: '1px solid var(--clr-border)', margin: '24px 0'}}></div>
            </div>
          )}
          
          <h4 style={{
            fontFamily: "var(--font-heading)",
            fontSize: '1.2rem',
            color: 'var(--clr-navy)',
            marginBottom: '16px'
          }}>Need Assistance?</h4>
          <p style={{fontSize: '0.9rem', color: 'var(--clr-text-light)', lineHeight: 1.6, marginBottom: '24px'}}>
            Our experts are ready to help you navigate your specific financial challenges.
          </p>
          <Link to="/contact" className="byhw-cta" style={{width: '100%', textAlign: 'center', marginBottom: '16px'}}>
            Schedule a Consultation
          </Link>
          <div style={{borderTop: '1px solid var(--clr-border)', margin: '24px 0'}}></div>
          <h4 style={{
            fontFamily: "var(--font-heading)",
            fontSize: '1.1rem',
            color: 'var(--clr-navy)',
            marginBottom: '16px'
          }}>Direct Contact</h4>
          <a href="mailto:Contact@byhwconsulting.com" style={{
            display: 'block',
            color: 'var(--clr-primary)',
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 500
          }}>Contact@byhwconsulting.com</a>
        </aside>
      </div>
    </PageTransition>
  );
}

export default ServiceDetail;